import React, { createContext, ReactNode, useEffect, useMemo, useState } from "react";
import {
  Company_User_Role,
  Exact,
  MeQuery,
  useMeQuery,
  UserCompaniesQuery,
  useUserCompaniesQuery,
} from "@generated/graphql";
import { ApolloError, ApolloQueryResult } from "@apollo/client";
import cookies from "js-cookie";
import { STUB_ROLES, WORKSPACE_ROLES } from "constants/access";
import { AUTH_STATE_CHANGE_EVENT } from "helpers/cookieHelper";
import { isPublicAuthRoute } from "constants/routes";

type CurrentCompanyUser = NonNullable<NonNullable<NonNullable<MeQuery["me"]>["userEmail"]["companyUsers"]>[number]>;

interface IAuthContext {
  me: MeQuery["me"] | undefined;
  meLoading: boolean;
  meError: ApolloError | undefined;
  meRefetch: (variables?: Partial<Exact<{ [key: string]: never }>>) => Promise<ApolloQueryResult<MeQuery>>;
  userCompanies: UserCompaniesQuery["userCompanies"] | undefined;
  userCompaniesLoading: boolean;
  userCompaniesError: ApolloError | undefined;
  userCompaniesRefetch: (
    variables?: Partial<Exact<{ [key: string]: never }>>
  ) => Promise<ApolloQueryResult<UserCompaniesQuery>>;
  currentCompanyUser: CurrentCompanyUser | undefined;
  currentCompanyRole: Company_User_Role | undefined;
  isSupervisor: boolean;
  hasWorkspaceAccess: boolean;
  isStubRole: boolean;
}

export const UserContext = createContext<IAuthContext>({
  me: undefined,
  meLoading: false,
  meError: undefined,
  meRefetch: async () => ({
    data: {
      me: {
        id: "",
        firstName: null,
        lastName: null,
        middleName: null,
        userEmail: { email: "", id: "", companyUsers: [] },
      },
    },
    loading: false,
    networkStatus: 7,
    stale: false,
  }),
  userCompanies: undefined,
  userCompaniesLoading: false,
  userCompaniesError: undefined,
  userCompaniesRefetch: async () => ({
    data: {
      userCompanies: [],
    },
    loading: false,
    networkStatus: 7,
    stale: false,
  }),
  currentCompanyUser: undefined,
  currentCompanyRole: undefined,
  isSupervisor: false,
  hasWorkspaceAccess: false,
  isStubRole: false,
});

export const UserProvider = ({ children }: { children: ReactNode }) => {
  const [authStateVersion, setAuthStateVersion] = useState(0);
  const { location } = window;
  const isPublicAuthPage = isPublicAuthRoute(location.pathname);
  const hasAuthTokens = useMemo(
    () => Boolean(cookies.get("accessToken") || cookies.get("refreshToken")),
    [authStateVersion]
  );
  const shouldSkipAuthBootstrap = isPublicAuthPage && !hasAuthTokens;

  useEffect(() => {
    const handleAuthStateChange = () => {
      setAuthStateVersion((prevState) => prevState + 1);
    };

    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);

    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);
    };
  }, []);

  const {
    data: meData,
    loading: meLoading,
    error: meError,
    refetch: meRefetch,
  } = useMeQuery({
    fetchPolicy: "network-only",
    skip: shouldSkipAuthBootstrap,
  });

  const {
    data: companyData,
    loading: userCompaniesLoading,
    error: userCompaniesError,
    refetch: userCompaniesRefetch,
  } = useUserCompaniesQuery({
    fetchPolicy: "network-only",
    skip: shouldSkipAuthBootstrap,
  });

  useEffect(() => {
    if (!authStateVersion || shouldSkipAuthBootstrap || !hasAuthTokens) {
      return;
    }

    Promise.allSettled([meRefetch(), userCompaniesRefetch()]);
  }, [authStateVersion, hasAuthTokens, meRefetch, shouldSkipAuthBootstrap, userCompaniesRefetch]);

  const me = hasAuthTokens ? meData?.me : undefined;
  const userCompanies = hasAuthTokens ? companyData?.userCompanies : undefined;
  const currentCompanyId = useMemo(
    () => (hasAuthTokens ? cookies.get("companyId") : undefined),
    [authStateVersion, hasAuthTokens]
  );
  const currentCompanyUser = (me?.userEmail.companyUsers || []).find(
    (companyUser) => companyUser.companyId === currentCompanyId
  );
  const currentCompanyRole = currentCompanyUser?.role;
  const isSupervisor = currentCompanyRole === Company_User_Role.Supervisor;
  const hasWorkspaceAccess = Boolean(currentCompanyRole && WORKSPACE_ROLES.includes(currentCompanyRole));
  const isStubRole = Boolean(currentCompanyRole && STUB_ROLES.includes(currentCompanyRole));

  const providerValue = useMemo(
    () => ({
      me,
      meLoading,
      meError,
      meRefetch,
      userCompanies,
      userCompaniesLoading,
      userCompaniesError,
      userCompaniesRefetch,
      currentCompanyUser,
      currentCompanyRole,
      isSupervisor,
      hasWorkspaceAccess,
      isStubRole,
    }),
    [
      me,
      meLoading,
      meError,
      meRefetch,
      userCompanies,
      userCompaniesLoading,
      userCompaniesError,
      userCompaniesRefetch,
      currentCompanyUser,
      currentCompanyRole,
      isSupervisor,
      hasWorkspaceAccess,
      isStubRole,
    ]
  );

  return <UserContext.Provider value={providerValue}>{children}</UserContext.Provider>;
};
